import type { AxiosInstance } from 'axios'
import {
  getWithPathFallback,
  pathWithTrailingSlashBeforeQuery,
  postWithPathFallback,
} from './httpHelpers'
import {
  pathDirectPatient,
  pathEncounterOutcome,
  pathFindPatient,
  pathProviderSearch,
  pathScheduleAppointment,
  pathSelectedPcp,
} from './openapiPaths'
import {
  AthenaPatientSchema,
  type AthenaPatient,
  DirectPatientResponseSchema,
  type DirectPatientResponse,
  GenericResponseSchema,
  type GenericResponse,
  ProviderSearchResponseSchema,
  type ProviderSearchResponse,
} from './swaggerSchemas'

export async function findPatient(
  client: AxiosInstance,
  params: {
    hospitalId: string
    patientName: string
    birthDate: string
    sessionId?: string
  },
): Promise<AthenaPatient> {
  const searchParams = new URLSearchParams({
    patient_name: params.patientName,
    birth_date: params.birthDate,
  })
  if (params.sessionId) {
    searchParams.set('session_id', params.sessionId)
  }

  const primary = `${pathFindPatient(params.hospitalId)}?${searchParams.toString()}`
  const alternate = pathWithTrailingSlashBeforeQuery(primary)

  const data = await getWithPathFallback(client, primary, alternate)
  return AthenaPatientSchema.parse(data)
}

export async function fetchDirectPatient(
  client: AxiosInstance,
  params: {
    tablespace: string
    athenaPatientId: string
  },
): Promise<DirectPatientResponse> {
  const primary = pathDirectPatient(params.tablespace, params.athenaPatientId)
  const alternate = primary.endsWith('/') ? primary : `${primary}/`

  const data = await getWithPathFallback(client, primary, alternate)
  return DirectPatientResponseSchema.parse(data)
}

export async function providerSearch(
  client: AxiosInstance,
  params: {
    hospitalId: string
    patientName: string
    patientId?: string | null
    tableSpace?: string | null
    birthDate: string
    expectedDischargeDate: string
    sessionId?: string
  },
): Promise<ProviderSearchResponse> {
  const searchParams = new URLSearchParams({
    patient_name: params.patientName,
    birth_date: params.birthDate,
    expected_discharge_date: params.expectedDischargeDate,
  })

  const patientId = params.patientId?.trim() ?? ''
  if (patientId) {
    searchParams.set('patient_id', patientId)
  }

  const tableSpace = params.tableSpace?.trim() ?? ''
  if (tableSpace) {
    searchParams.set('tablespace', tableSpace)
  }

  if (params.sessionId) {
    searchParams.set('session_id', params.sessionId)
  }

  const primary = `${pathProviderSearch(params.hospitalId)}?${searchParams.toString()}`
  const alternate = pathWithTrailingSlashBeforeQuery(primary)

  const data = await getWithPathFallback(client, primary, alternate)
  return ProviderSearchResponseSchema.parse(data)
}

export async function postEncounterOutcome(
  client: AxiosInstance,
  params: {
    hospitalId: string
    exitTrigger: 'acknowledge_and_close' | 'start_new_encounter'
    reportedAppointmentScheduled: boolean
    wrapCode: string
    additionalDetails?: string
  },
): Promise<void> {
  const primary = pathEncounterOutcome(params.hospitalId)
  const alternate = primary.endsWith('/') ? primary : `${primary}/`

  const body: Record<string, unknown> = {
    exit_trigger: params.exitTrigger,
    reported_appointment_scheduled: params.reportedAppointmentScheduled,
    wrap_code: params.wrapCode,
  }
  if (params.additionalDetails != null) {
    body.additional_details = params.additionalDetails
  }

  await postWithPathFallback(client, primary, alternate, body)
}

export async function postSelectedPcp(
  client: AxiosInstance,
  params: {
    hospitalId: string
    providerName: string
    providerNickname: string | null
    providerRmId: string
  },
): Promise<void> {
  const primary = pathSelectedPcp(params.hospitalId)
  const alternate = primary.endsWith('/') ? primary : `${primary}/`

  const body: Record<string, unknown> = {
    provider_name: params.providerName,
    provider_rm_id: params.providerRmId,
  }
  if (params.providerNickname != null) {
    body.provider_nickname = params.providerNickname
  }

  await postWithPathFallback(client, primary, alternate, body)
}

export async function scheduleAppointment(
  client: AxiosInstance,
  params: {
    tablespace: string
    sessionId?: string
    body: {
      patient_id: string
      slot_id: string
      physician_id: string
      appointment_type: string
      appointment_notes: string
      hospital_id: string
      appointment_time?: string | null
      appointment_location?: string | null
      appointment_provider?: string | null
    }
  },
): Promise<GenericResponse> {
  const primary = pathScheduleAppointment(params.tablespace)
  const alternate = primary.endsWith('/') ? primary : `${primary}/`

  const body = params.sessionId
    ? { ...params.body, session_id: params.sessionId }
    : params.body

  const data = await postWithPathFallback(client, primary, alternate, body)
  return GenericResponseSchema.parse(data)
}
